// Notification STYLES: four named bundles of the member's own defaults.
//
// Decided 2026-10-20 (Will): "if they want more phone app push, the template would set it
// up for that; if more of an email way, that one; if more like text messages, that one;
// and the website app push, computer or phone, should be one as well".
//
// A style is not a new layer and not a column. It is a list of cells sent through the
// EXISTING `PATCH /me/notification-settings/defaults` (so the masquerade deny-list and the
// "cell equal to the system default deletes its row" rule already apply). Which style a
// member is on is DERIVED by `matchingPreset()` comparing their resolved grid to each
// style, so nothing records a choice that could drift from the cells it set.
//
// A style OWNS every offered cell: applying one replaces the member's own defaults, and
// the screen says so. Per-group overrides are untouched.
//
// Honest limits, which the screen repeats:
//   * PUSH IS PER DEVICE. A style can turn push ON as a choice; whether this phone or
//     browser receives anything depends on that device granting permission.
//   * TEXT needs a verified phone, and is limited to the kinds the registered campaign
//     allows (`isOffered`). The Texts style never leaves a kind silent: where text is not
//     offered it falls back to email.
//   * Text messages carry NO member content (the A2P registration says so), so the Texts
//     style is a nudge, not a transcript. Content rides in push.
import { NOTIF_TYPES, SMS_CAMPAIGN_AMENDED, TEXT_SIGNUP_SMS_ON, isOffered, } from './notifyModel.js';
export const NOTIFICATION_PRESET_IDS = ['phone', 'computer', 'email', 'text'];
export function isPresetId(v) {
    return typeof v === 'string' && NOTIFICATION_PRESET_IDS.includes(v);
}
export const NOTIFICATION_PRESETS = {
    phone: {
        id: 'phone',
        label: 'Phone app',
        blurb: 'Push for everything, on your phone. Email only for the day-of check, new events, event changes and join requests.',
        needsPhone: false,
    },
    computer: {
        id: 'computer',
        label: 'Computer',
        blurb: 'Push in this browser for everything, with email as the backup for the time-critical kinds.',
        needsPhone: false,
    },
    email: {
        id: 'email',
        label: 'Email',
        blurb: 'Everything by email, nothing by push (except table assignments, which are push only).',
        needsPhone: false,
    },
    text: {
        id: 'text',
        label: 'Text messages',
        blurb: 'Texts where text is allowed, email where it is not, so nothing goes quiet. Texts say there is news and link to it.',
        needsPhone: true,
    },
};
/** The kinds the day-of safety net always keeps an email for. It is the app's reason to exist. */
const DAY_OF = 'dayOf';
/** What one style chooses for one (type, channel). Only called for OFFERED cells. */
function choose(id, type, channel, campaignAmended) {
    switch (id) {
        case 'phone': {
            if (channel === 'sms')
                return false;
            if (channel === 'push')
                return true;
            return type === DAY_OF || type === 'newEvent' || type === 'eventChanged' || type === 'joinRequest';
        }
        case 'computer': {
            if (channel === 'sms')
                return false;
            if (channel === 'push')
                return true;
            // The time-critical kinds keep their email: a browser tab that is closed or asleep
            // receives nothing.
            return (type === DAY_OF ||
                type === 'newEvent' ||
                type === 'headcount' ||
                type === 'eventOn' ||
                type === 'eventFull' ||
                type === 'eventChanged' ||
                type === 'joinRequest');
        }
        case 'email': {
            if (channel === 'sms')
                return false;
            if (channel === 'email')
                return true;
            // Push only where it is the ONLY channel offered (a table number is not worth a mail).
            return !isOffered(type, 'email', campaignAmended);
        }
        case 'text': {
            const smsOn = isOffered(type, 'sms', campaignAmended) && TEXT_SIGNUP_SMS_ON.includes(type);
            if (channel === 'sms')
                return smsOn;
            if (channel === 'email') {
                // Never silent: email stays wherever a text is not chosen, and the day-of check
                // keeps its email as well as its text (belt and braces).
                return type === DAY_OF || !smsOn;
            }
            // Push stays only where it is the only channel offered.
            return !isOffered(type, 'email', campaignAmended) && !isOffered(type, 'sms', campaignAmended);
        }
    }
}
/**
 * Every OFFERED cell a style sets, as the changes to send. A cell that equals the system
 * default is included on purpose: the server deletes that row, which is how a style
 * replaces whatever the member had chosen before.
 */
export function presetCells(id, campaignAmended = SMS_CAMPAIGN_AMENDED) {
    const cells = [];
    for (const type of NOTIF_TYPES) {
        for (const channel of ['email', 'sms', 'push']) {
            if (!isOffered(type, channel, campaignAmended))
                continue;
            cells.push({ type, channel, enabled: choose(id, type, channel, campaignAmended) });
        }
    }
    return cells;
}
/**
 * Which style the member is on, or null for "custom". Compares the RESOLVED grid (own
 * default over system default) against each style, cell by cell, over offered cells only.
 */
export function matchingPreset(grid, campaignAmended = SMS_CAMPAIGN_AMENDED) {
    for (const id of NOTIFICATION_PRESET_IDS) {
        const cells = presetCells(id, campaignAmended);
        if (cells.every((c) => grid[c.type][c.channel]?.enabled === c.enabled))
            return id;
    }
    return null;
}
//# sourceMappingURL=notifyPresets.js.map